import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { formatPercentPoints } from '@shared';
import {
  createCatalogueBootstrapService,
  type BootstrapCandidate,
  type BootstrapImportBatchResult,
} from '../lib/catalogueBootstrap';
import { getProductRepository } from '../lib/productRepository';
import { ErrorState, LoadingState, PrimaryButton, SecondaryButton } from '../components/ui';
import { legacyServerToolsEnabled } from '../lib/runtimeFeatures';

function candidateTitle(c: BootstrapCandidate): string {
  return (
    c.source.name ??
    c.source.productName ??
    c.source.productId ??
    c.source.id
  );
}

/**
 * Local-mode one-way import from the existing server-backed catalogue into
 * this device's IndexedDB ProductRepository.
 */
export function ImportCatalogueBootstrap(): JSX.Element {
  const navigate = useNavigate();
  const activeMode = getProductRepository().mode;
  const migrationEnabled = legacyServerToolsEnabled();
  const service = useMemo(() => createCatalogueBootstrapService(), []);

  const [candidates, setCandidates] = useState<BootstrapCandidate[]>([]);
  const [selected, setSelected] = useState<Set<string>>(() => new Set());
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [batch, setBatch] = useState<BootstrapImportBatchResult | null>(null);

  const reload = async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const rows = await service.listCandidates({
        archived: 'active',
        sort: 'name',
      });
      setCandidates(rows);
      setSelected(new Set());
    } catch (err: unknown) {
      setError(
        err instanceof Error
          ? err.message
          : 'Could not load the existing server catalogue.',
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (activeMode !== 'local' || !migrationEnabled) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    service
      .listCandidates({ archived: 'active', sort: 'name' })
      .then((rows) => {
        if (cancelled) return;
        setCandidates(rows);
        setSelected(new Set());
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(
          err instanceof Error
            ? err.message
            : 'Could not load the existing server catalogue.',
        );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [activeMode, migrationEnabled, service]);

  if (!migrationEnabled) {
    return (
      <div className="stack">
        <h1 className="page-title">Import Existing Catalogue</h1>
        <div className="notice" role="status">
          Legacy server import is disabled in this build. Personal products are
          stored only on this device.
        </div>
        <Link to="/add" className="btn">← Back to Add Product</Link>
      </div>
    );
  }

  if (activeMode !== 'local') {
    return (
      <div className="stack">
        <h1 className="page-title">Import Existing Catalogue</h1>
        <div className="notice" role="status">
          This import path is available only when the app is running in local
          product repository mode. Server mode continues to use Settings →
          Catalogue Sync for spreadsheet pulls.
        </div>
        <Link to="/add" className="btn">
          ← Back to Add Product
        </Link>
      </div>
    );
  }

  const available = candidates.filter((c) => c.status === 'available');
  const availableIds = available.map((c) => c.source.id);

  const toggle = (id: string): void => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectAllAvailable = (): void => {
    setSelected(new Set(availableIds));
  };

  const clearSelection = (): void => {
    setSelected(new Set());
  };

  const runImport = async (ids: string[]): Promise<void> => {
    if (ids.length === 0) return;
    setBusy(true);
    setBatch(null);
    setError(null);
    try {
      // Confirm active repository was not switched.
      if (getProductRepository().mode !== 'local') {
        throw new Error('Active product repository is no longer local.');
      }
      const result = await service.importSelected(ids);
      setBatch(result);
      await reload();
    } catch (err: unknown) {
      setError(
        err instanceof Error ? err.message : 'Import failed unexpectedly.',
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="stack">
      <button type="button" className="btn" onClick={() => navigate('/add')}>
        ← Back
      </button>
      <h1 className="page-title">Import Existing Catalogue</h1>
      <p className="page-lead">
        Copy products from the current server-backed catalogue into this
        device&apos;s local library. This is a one-way bootstrap — it does not
        change Google Sheets, sync status, or server records.
      </p>

      {loading ? <LoadingState label="Loading server catalogue…" /> : null}
      {error ? <ErrorState message={error} /> : null}

      {!loading && !error ? (
        <>
          <div className="bootstrap-toolbar">
            <SecondaryButton
              type="button"
              onClick={selectAllAvailable}
              disabled={busy || availableIds.length === 0}
            >
              Select all available
            </SecondaryButton>
            <SecondaryButton
              type="button"
              onClick={clearSelection}
              disabled={busy || selected.size === 0}
            >
              Clear
            </SecondaryButton>
            <PrimaryButton
              type="button"
              onClick={() => void runImport([...selected])}
              disabled={busy || selected.size === 0}
            >
              Import selected ({selected.size})
            </PrimaryButton>
            <PrimaryButton
              type="button"
              onClick={() => void runImport(availableIds)}
              disabled={busy || availableIds.length === 0}
            >
              Import all available ({availableIds.length})
            </PrimaryButton>
          </div>

          {candidates.length === 0 ? (
            <p className="muted">No active products found on the server catalogue.</p>
          ) : (
            <ul className="bootstrap-list">
              {candidates.map((c) => {
                const id = c.source.id;
                const disabled = c.status !== 'available' || busy;
                const checked = selected.has(id);
                return (
                  <li key={id} className={`bootstrap-row bootstrap-row--${c.status}`}>
                    <label className="bootstrap-row__label">
                      <input
                        type="checkbox"
                        checked={checked}
                        disabled={disabled}
                        onChange={() => toggle(id)}
                      />
                      <span className="bootstrap-row__body">
                        <strong>{candidateTitle(c)}</strong>
                        <span className="muted bootstrap-row__meta">
                          {c.source.brand ?? c.source.producer ?? '—'}
                          {' · '}
                          {formatPercentPoints(c.source.thcPercent)} THC
                          {c.source.productId
                            ? ` · ${c.source.productId}`
                            : ''}
                        </span>
                        <span className={`bootstrap-status bootstrap-status--${c.status}`}>
                          {c.status === 'available'
                            ? 'Available to import'
                            : c.status === 'already_imported'
                              ? 'Already imported'
                              : 'Potential conflict'}
                        </span>
                      </span>
                    </label>
                  </li>
                );
              })}
            </ul>
          )}
        </>
      ) : null}

      {batch ? (
        <div
          className={`notice bootstrap-summary bootstrap-summary--${
            batch.failedCount > 0 ? 'error' : 'success'
          }`}
          role="status"
        >
          <p>
            Import finished: {batch.importedCount} imported,{' '}
            {batch.skippedCount} already imported, {batch.conflictCount}{' '}
            conflict, {batch.failedCount} failed.
          </p>
          {batch.results.some((r) => r.outcome === 'failed') ? (
            <ul>
              {batch.results
                .filter((r) => r.outcome === 'failed')
                .map((r) => (
                  <li key={r.sourceId}>
                    {r.sourceLabel}: {r.error ?? 'failed'}
                  </li>
                ))}
            </ul>
          ) : null}
          <Link to="/catalogue" className="btn">
            Open Library
          </Link>
        </div>
      ) : null}
    </div>
  );
}
